import { HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { catchError, filter, first, Observable, of, take, tap } from 'rxjs';
import { RealtimeSpaceService } from 'src/app/services/realtime-space.service';
import { SessionSharedDataService } from 'src/app/services/session-shared-data.service';
import { SpaceBoardsService } from 'src/app/services/space-boards.service';
import { SpacesService } from 'src/app/services/spaces.service';
import { SpaceRepository } from 'src/app/state/space.repository';
import { BoardFolder, Frame } from 'src/app/models/session';
import { ColorScheme } from 'src/app/standalones/components/pencil-bottom-sheet-container/pencil-bottom-sheet-container.component';
import { Collaboration } from '@pncl/common-models';
import { ExtendedTextboxType } from '../../../services/wb-top-controls-v2.service';
import { RenameTitleBottomSheetComponent } from '../wb-top-controls-v2/components/commons/rename-title-bottom-sheet/rename-title-bottom-sheet.component';

export const BOARD_NAME_MAX_LENGTH = 80;
export const ROOM_NAME_MAX_LENGTH = 50;
export const SPACE_NAME_MAX_LENGTH = Collaboration.Constants.MAX_ENTITY_LONG_TITLE_LENGTH;
export const FOLDER_NAME_MAX_LENGTH = 25;

@Injectable({
  providedIn: 'root',
})
export class RenameTitlesService {
  constructor(
    private spaceRepo: SpaceRepository,
    private sessionSharedDataService: SessionSharedDataService,
    private spacesService: SpacesService,
    private realtimeSpaceService: RealtimeSpaceService,
    private spaceBoardsService: SpaceBoardsService,
    private bottomSheet: MatBottomSheet,
    private translate: TranslateService,
  ) {}

  renameSpace(title: string): Observable<HttpResponse<Object>> | undefined {
    if (!this.spaceRepo.activeSpace?.title || this.spaceRepo.activeSpace?.title === title) {
      return;
    }
    const prevTitle = this.spaceRepo.activeSpace?.title;

    const spaceId = this.spaceRepo.activeSpace._id;

    this.sessionSharedDataService.titleUpdate.next({
      id: spaceId,
      title,
    });

    return this.spacesService.updateTitle(spaceId, title).pipe(
      first(),
      filter((res) => res.ok),
      catchError((err) => {
        this.sessionSharedDataService.titleUpdate.next({
          id: spaceId,
          title: prevTitle,
        });

        return of(err);
      }),
    );
  }

  renameRoom(title: string): void {
    const room = this.spaceRepo.activeSpaceCurrentRoom;
    if (!room || room.name === title) {
      return;
    }

    room.name = title;
    this.realtimeSpaceService.service.modifyRoom(room, this.spaceRepo.activeSpaceId as string);
  }

  renameBoard(title: string, selectedBoard?: Frame): void {
    const frame = selectedBoard ?? this.spaceBoardsService.getActiveSpaceSelectedBoard();
    if (
      !frame ||
      !this.spaceRepo.activeSpaceId ||
      !this.spaceRepo.activeSpaceCurrentRoomUid ||
      !this.sessionSharedDataService.canModifySession()
    ) {
      return;
    }

    frame.name = title;
    this.realtimeSpaceService.service.modifyFrame(
      frame,
      this.spaceRepo.activeSpaceId,
      this.spaceRepo.activeSpaceCurrentRoomUid,
    );
  }

  renameBoardFolder(title: string, selectedBoardFolder: BoardFolder): void {
    if (
      !selectedBoardFolder ||
      !this.spaceRepo.activeSpaceId ||
      !this.spaceRepo.activeSpaceCurrentRoomUid ||
      !this.sessionSharedDataService.canModifySession()
    ) {
      return;
    }

    selectedBoardFolder.name = title.trim();
    this.realtimeSpaceService.service.modifyBoardFolder(
      selectedBoardFolder,
      this.spaceRepo.activeSpaceId,
      this.spaceRepo.activeSpaceCurrentRoomUid,
    );
  }

  private getTitleByTextBoxType(
    type: ExtendedTextboxType,
    selectedFrameOrBoardFolder?: Frame | BoardFolder,
  ): string {
    if (ExtendedTextboxType.BOARD && selectedFrameOrBoardFolder) {
      return selectedFrameOrBoardFolder?.name ?? '';
    }

    switch (type) {
      case ExtendedTextboxType.SPACE:
        return this.spaceRepo.activeSpace?.title ?? '';
      case ExtendedTextboxType.ROOM:
        return this.spaceRepo.activeSpaceCurrentRoom?.name ?? '';
      case ExtendedTextboxType.BOARD:
        return this.spaceBoardsService.getActiveSpaceSelectedBoard()?.name ?? '';
      case ExtendedTextboxType.FOLDER:
        return selectedFrameOrBoardFolder?.name ?? '';
    }
  }

  private getBottomSheetContainerTitle(type: ExtendedTextboxType): string {
    switch (type) {
      case ExtendedTextboxType.SPACE:
        return 'Rename Space';
      case ExtendedTextboxType.ROOM:
        return 'Rename room';
      case ExtendedTextboxType.BOARD:
        return 'Rename board';
      case ExtendedTextboxType.FOLDER:
        return 'Rename folder';
    }
  }

  /**
   *
   * @param type Space, Room, Board, or Folder
   * @param frameOrBoardFolder
   *    Frame object: if type is BOARD, and the target board to rename is not the current active
   *    BoardFolder object if type is Folder
   * @param colorScheme default is DARK
   */
  openRenameInputBottomSheet(
    type: ExtendedTextboxType,
    frameOrBoardFolder?: Frame | BoardFolder,
    colorScheme?: ColorScheme,
  ): Observable<{ returnToPreviousBottomSheet: boolean; output: string | null }> {
    const panelClass =
      colorScheme === ColorScheme.LIGHT
        ? ['pencil-bottom-sheet', 'light-scheme']
        : ['pencil-bottom-sheet'];
    return this.bottomSheet
      .open(RenameTitleBottomSheetComponent, {
        data: {
          value: this.getTitleByTextBoxType(type, frameOrBoardFolder),
          title: this.translate.instant(this.getBottomSheetContainerTitle(type)),
          colorScheme,
          maxLength: this.getMaxLengthByTextBoxType(type),
        },
        panelClass,
        closeOnNavigation: true,
      })
      .afterDismissed()
      .pipe(
        take(1),
        tap((data: { returnToPreviousBottomSheet: boolean; output: string | null }) => {
          if (data?.output) {
            switch (type) {
              case ExtendedTextboxType.SPACE:
                this.renameSpace(data.output)?.subscribe();
                break;
              case ExtendedTextboxType.ROOM:
                this.renameRoom(data.output);
                break;
              case ExtendedTextboxType.BOARD:
                this.renameBoard(data.output, frameOrBoardFolder as Frame);
                break;
              case ExtendedTextboxType.FOLDER:
                this.renameBoardFolder(data.output, frameOrBoardFolder as BoardFolder);
                break;
            }
          }
        }),
      );
  }
  getMaxLengthByTextBoxType(type: ExtendedTextboxType): number {
    switch (type) {
      case ExtendedTextboxType.SPACE:
        return SPACE_NAME_MAX_LENGTH;
      case ExtendedTextboxType.ROOM:
        return ROOM_NAME_MAX_LENGTH;
      case ExtendedTextboxType.BOARD:
        return BOARD_NAME_MAX_LENGTH;
      case ExtendedTextboxType.FOLDER:
        return FOLDER_NAME_MAX_LENGTH;
    }
  }
}
